import { MARKER_ICONS, getIconDef } from './markerIcons';
import { STATUS_CONFIG } from './utils';
import { BADGE_PADDING, BADGE_PADDING_TIGHT } from '@/components/map/labelLayerFilters';

// Derived from STATUS_CONFIG (the 5-status palette) so a new status can never
// drift out of the pre-generated marker-image set. Hardcoding this list left
// `pending` (added 2026-05-23) uncovered, so colored/ring markers on pending
// vehicles referenced an unregistered image and rendered nothing. MapView's
// STATUS_COLORS is derived the same way — keep them in lockstep by derivation.
export const STATUSES = Object.keys(STATUS_CONFIG);
const STATUS_COLORS = Object.fromEntries(
  STATUSES.map((status) => [status, STATUS_CONFIG[status].color]),
);

/**
 * Render a predefined SVG icon onto a canvas in a given color.
 * Returns MapLibre-compatible image data { width, height, data }.
 */
export function renderColoredIcon(iconKey, color, size = 36) {
  const def = getIconDef(iconKey);
  if (!def) return null;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const scale = size / 24;
  ctx.scale(scale, scale);
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  for (const d of def.paths) {
    const p = new Path2D(d);
    ctx.stroke(p);
  }
  for (const c of (def.circles || [])) {
    ctx.beginPath();
    ctx.arc(c.cx, c.cy, c.r, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  return { width: size, height: size, data: new Uint8Array(ctx.getImageData(0, 0, size, size).data) };
}

/**
 * Render the ring + pointer image (no icon inside).
 * Pointer at top (North / 0°). MapLibre rotates via icon-rotate.
 */
export function renderRingImage(color, size = 56) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const cx = size / 2;
  const radius = size / 2 - 10; // leave room for the large arrow pointer

  // Translucent fill circle
  ctx.beginPath();
  ctx.arc(cx, cx, radius, 0, Math.PI * 2);
  // Use globalAlpha for cross-browser compat (8-digit hex not supported everywhere)
  ctx.globalAlpha = 0.25;
  ctx.fillStyle = color;
  ctx.fill();
  ctx.globalAlpha = 1.0; // reset for border + pointer
  // Colored border
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.5;
  ctx.stroke();

  // Large arrow pointer at top (North) — must be clearly visible as direction indicator
  const tipY = cx - radius - 5;
  ctx.beginPath();
  ctx.moveTo(cx, tipY);              // tip of arrow
  ctx.lineTo(cx - 8, tipY + 13);     // bottom-left
  ctx.lineTo(cx, tipY + 9);          // notch
  ctx.lineTo(cx + 8, tipY + 13);     // bottom-right
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.fill();
  // White edge for contrast
  ctx.strokeStyle = 'rgba(255,255,255,0.8)';
  ctx.lineWidth = 1;
  ctx.stroke();

  return { width: size, height: size, data: new Uint8Array(ctx.getImageData(0, 0, size, size).data) };
}

/**
 * Render the ring without pointer (for offline / non-directional).
 */
export function renderRingNoPointer(color, size = 56) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const cx = size / 2;
  const radius = size / 2 - 10;
  ctx.beginPath();
  ctx.arc(cx, cx, radius, 0, Math.PI * 2);
  ctx.globalAlpha = 0.25;
  ctx.fillStyle = color;
  ctx.fill();
  ctx.globalAlpha = 1.0;
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.5;
  ctx.stroke();
  return { width: size, height: size, data: new Uint8Array(ctx.getImageData(0, 0, size, size).data) };
}

/**
 * Render white icon with dark outline on transparent canvas (for ring icon overlay layer).
 * The dark outline ensures visibility on light map tiles.
 */
export function renderRingIconOverlay(iconKey, size = 56) {
  const def = getIconDef(iconKey);
  if (!def) return null;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const iconSize = size * 0.55;
  const offset = (size - iconSize) / 2;
  ctx.translate(offset, offset);
  const scale = iconSize / 24;
  ctx.scale(scale, scale);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // First pass: dark outline for contrast on light map tiles
  ctx.strokeStyle = 'rgba(0,0,0,0.4)';
  ctx.lineWidth = 3.2;
  for (const d of def.paths) {
    ctx.stroke(new Path2D(d));
  }
  for (const c of (def.circles || [])) {
    ctx.beginPath();
    ctx.arc(c.cx, c.cy, c.r, 0, Math.PI * 2);
    ctx.stroke();
  }

  // Second pass: white icon on top
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.6;
  for (const d of def.paths) {
    ctx.stroke(new Path2D(d));
  }
  for (const c of (def.circles || [])) {
    ctx.beginPath();
    ctx.arc(c.cx, c.cy, c.r, 0, Math.PI * 2);
    ctx.stroke();
  }

  ctx.setTransform(1, 0, 0, 1, 0, 0);
  return { width: size, height: size, data: new Uint8Array(ctx.getImageData(0, 0, size, size).data) };
}

/**
 * Pre-generate all needed images for a set of devices and add them to the map.
 * Called lazily when devices change.
 */
export function ensureMarkerImages(map, devices, loadedImages) {
  for (const device of Object.values(devices)) {
    const attrs = device.attributes || {};
    const style = attrs.mapMarkerStyle || (attrs.mapMarker === 'icon' ? 'colored' : 'arrow');
    const iconKey = attrs.mapMarkerIcon;

    if (style === 'arrow' || !iconKey) continue;

    if (style === 'colored' && iconKey !== 'custom') {
      for (const status of STATUSES) {
        const key = `icon-${iconKey}-${status}`;
        if (!loadedImages.has(key)) {
          const img = renderColoredIcon(iconKey, STATUS_COLORS[status]);
          if (img && !map.hasImage(key)) {
            map.addImage(key, img);
          }
          loadedImages.add(key);
        }
      }
    }

    if (style === 'ring' && iconKey !== 'custom') {
      const def = getIconDef(iconKey);
      for (const status of STATUSES) {
        const needsPointer = def?.directional && status !== 'offline';
        const ringKey = needsPointer ? `ring-${status}` : `ring-nopointer-${status}`;
        if (!loadedImages.has(ringKey)) {
          const img = needsPointer
            ? renderRingImage(STATUS_COLORS[status])
            : renderRingNoPointer(STATUS_COLORS[status]);
          if (img && !map.hasImage(ringKey)) {
            map.addImage(ringKey, img);
          }
          loadedImages.add(ringKey);
        }
      }
      const iconOverlayKey = `ring-icon-${iconKey}`;
      if (!loadedImages.has(iconOverlayKey)) {
        const img = renderRingIconOverlay(iconKey);
        if (img && !map.hasImage(iconOverlayKey)) {
          map.addImage(iconOverlayKey, img);
        }
        loadedImages.add(iconOverlayKey);
      }
    }
  }
}

// ── Label badges (spec §5) ────────────────────────────────────────────────────
// A 9-slice image stretched by MapLibre's `icon-text-fit` to wrap the label text.
// One image per (shape, colour); opacity is applied by the layer's icon-opacity,
// so the image itself is opaque.
const BADGE_SIZE = 24;      // logical px, square
const BADGE_RATIO = 2;      // drawn at 2x for crisp corners
export function badgeImageId(shape, hex) {
  return `label-badge-${shape}-${String(hex).replace('#', '').toLowerCase()}`;
}

/**
 * Pure 9-slice geometry for a badge image. Pill: the corners are fully round, so
 * stretch only a thin central strip and inset the text box by a quarter of the
 * image on each side. Rect: keep the corner radius clear of the stretch band.
 */
export function badgeSliceMetrics(shape) {
  const px = BADGE_SIZE * BADGE_RATIO;
  const radius = (shape === 'pill' ? BADGE_SIZE / 2 : 4) * BADGE_RATIO;
  if (shape === 'pill') {
    return {
      px,
      radius,
      stretchX: [[px / 2 - 1, px / 2 + 1]],
      stretchY: [[px / 2 - 1, px / 2 + 1]],
      content: [px / 4, px / 4, px - px / 4, px - px / 4],
    };
  }
  const edge = Math.ceil(radius) + 2;
  return {
    px,
    radius,
    stretchX: [[edge, px - edge]],
    stretchY: [[edge, px - edge]],
    content: [edge, edge, px - edge, px - edge],
  };
}

export function renderBadgeImage(shape, hex) {
  const { px, radius, stretchX, stretchY, content } = badgeSliceMetrics(shape);
  const canvas = document.createElement('canvas');
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.fillStyle = hex;
  ctx.beginPath();
  ctx.moveTo(radius, 0);
  ctx.lineTo(px - radius, 0);
  ctx.quadraticCurveTo(px, 0, px, radius);
  ctx.lineTo(px, px - radius);
  ctx.quadraticCurveTo(px, px, px - radius, px);
  ctx.lineTo(radius, px);
  ctx.quadraticCurveTo(0, px, 0, px - radius);
  ctx.lineTo(0, radius);
  ctx.quadraticCurveTo(0, 0, radius, 0);
  ctx.closePath();
  ctx.fill();
  return {
    image: { width: px, height: px, data: new Uint8Array(ctx.getImageData(0, 0, px, px).data) },
    options: { pixelRatio: BADGE_RATIO, stretchX, stretchY, content },
  };
}

export const BADGE_GLOW_COLOR = '#3b82f6';

/** Register the two selection-glow images (pill + rect in the glow colour); idempotent. */
export function ensureGlowBadgeImages(map, render = renderBadgeImage) {
  for (const shape of ['pill', 'rect']) {
    const id = badgeImageId(shape, BADGE_GLOW_COLOR);
    if (map.hasImage(id)) continue;
    const rendered = render(shape, BADGE_GLOW_COLOR);
    if (rendered) map.addImage(id, rendered.image, rendered.options);
  }
}

/** Register a badge image for every distinct (shape, colour) in `features`; idempotent. */
export function ensureBadgeImages(map, features, render = renderBadgeImage) {
  const seen = new Set();
  for (const f of features || []) {
    const p = f?.properties;
    if (!p || p.kind !== 'vehicle' || !p.labelShape || p.labelShape === 'none' || !p.labelBg) continue;
    const id = badgeImageId(p.labelShape, p.labelBg);
    if (seen.has(id) || map.hasImage(id)) continue;
    seen.add(id);
    const rendered = render(p.labelShape, p.labelBg);
    if (rendered) map.addImage(id, rendered.image, rendered.options);
  }
}

// ── Label cards ("Stack as cards", spec 2026-10-30 §3.3) ─────────────────────
// One baked image per card: background, text and (when selected) a blue ring, so the
// card layer is icon-only and a card on top hides the one beneath it completely.
// Coordinates are scaled by hand (no ctx.scale) so the renderer only needs the plain
// drawing calls. Fonts and sizes mirror the badge layer: bold, line height 1.25,
// one notch smaller text and the tight padding when the style is compact.
const CARD_FONT = '"DM Sans", "Noto Sans", sans-serif';
const CARD_SELECTED_STROKE = 2;
// Strong right-to-left letters (Hebrew, Arabic incl. Sorani, presentation forms);
// Arabic-Indic digits are weak and excluded.
const RTL_STRONG = /[\u0590-\u05FF\u0600-\u065F\u066A-\u06EF\u06FA-\u08FF\uFB1D-\uFDFF\uFE70-\uFEFF]/;
const LETTER = /\p{L}/u;

function firstStrongDirection(text) {
  for (const ch of text) {
    if (RTL_STRONG.test(ch)) return 'rtl';
    if (LETTER.test(ch)) return 'ltr';
  }
  return 'ltr';
}

function roundedRectPath(ctx, x, y, w, h, r) {
  const rr = Math.min(r, h / 2, w / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

/** Bake one label card. Returns `{ image, options }` for `map.addImage`, or null without a 2D context. */
export function renderCardImage(spec) {
  const dpr = Math.min(3, (typeof window !== 'undefined' && window.devicePixelRatio) || 1);
  const tight = spec.tight === 1 || spec.tight === true;
  const basePx = Number(spec.textPx) || 11;
  const textPx = tight ? basePx - 1 : basePx;
  const lineH = Math.round(textPx * 1.25);
  const [padT, padR, padB, padL] = tight ? BADGE_PADDING_TIGHT : BADGE_PADDING;
  const lines = String(spec.text ?? '').split('\n');
  const plain = spec.shape === 'none';
  const bg = plain ? '#ffffff' : spec.bg;
  const margin = spec.selected ? CARD_SELECTED_STROKE : 0;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.font = `bold ${textPx}px ${CARD_FONT}`;
  let textW = 0;
  for (const line of lines) textW = Math.max(textW, ctx.measureText(line).width);
  const w = Math.ceil(textW) + padL + padR;
  const h = lines.length * lineH + padT + padB;
  const W = Math.round((w + 2 * margin) * dpr);
  const H = Math.round((h + 2 * margin) * dpr);
  canvas.width = W;
  canvas.height = H;
  // Resizing a canvas resets its state, so (re)apply everything after it.
  const radius = plain || spec.shape === 'pill' ? h / 2 : 4;

  // Always opaque: a stacked card must fully hide the card beneath it, so the
  // style's background opacity does not apply in Stack mode.
  ctx.globalAlpha = 1;
  ctx.fillStyle = bg;
  roundedRectPath(ctx, margin * dpr, margin * dpr, w * dpr, h * dpr, radius * dpr);
  ctx.fill();

  if (spec.selected) {
    ctx.globalAlpha = 1;
    ctx.strokeStyle = BADGE_GLOW_COLOR;
    ctx.lineWidth = CARD_SELECTED_STROKE * dpr;
    const half = CARD_SELECTED_STROKE / 2;
    roundedRectPath(ctx, half * dpr, half * dpr, (w + CARD_SELECTED_STROKE) * dpr, (h + CARD_SELECTED_STROKE) * dpr, (radius + half) * dpr);
    ctx.stroke();
  }

  ctx.globalAlpha = 1;
  // Plain cards match the plain label layer's slate text, not the style colour.
  ctx.fillStyle = plain ? '#334155' : spec.fg;
  ctx.font = `bold ${textPx * dpr}px ${CARD_FONT}`;
  ctx.direction = firstStrongDirection(lines.join(' '));
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const cx = (margin + w / 2) * dpr;
  lines.forEach((line, i) => {
    ctx.fillText(line, cx, (margin + padT + lineH * (i + 0.5)) * dpr);
  });

  return {
    image: { width: W, height: H, data: new Uint8Array(ctx.getImageData(0, 0, W, H).data) },
    options: { pixelRatio: dpr },
  };
}
